import { useEffect, useRef, useState, useCallback } from "react";
import { User as UserIcon, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Public grid - hourly rows. Cells may indicate a "start_minute=30" case with "inizio 09:30".
 */
export function PublicAgendaGrid({ weekData, selection, onCellClick }) {
    const hoursLabels = weekData?.hours_labels || [];
    const days = weekData?.days || [];

    const cellClass = (hourObj, day) => {
        const isSel = selection && selection.date === day.date && selection.startHour === hourObj.hour;
        const isNext = selection && selection.date === day.date && (selection.startHour + 1) === hourObj.hour;
        if (hourObj.status === "available") {
            let base = "agenda-cell agenda-cell-available";
            if (isSel) base += " agenda-cell-selected agenda-cell-selected-top";
            if (isNext) base += " agenda-cell-selected agenda-cell-selected-bottom";
            return base;
        }
        return "agenda-cell agenda-cell-unavailable";
    };

    return (
        <div className="w-full overflow-x-auto" data-testid="agenda-grid-desktop">
            <table className="w-full border-collapse" style={{ minWidth: 720 }}>
                <thead>
                    <tr>
                        <th className="w-20 border border-gray-200 bg-white text-xs font-bold text-gray-700 py-2">Ora</th>
                        {days.map((d) => (
                            <th key={d.date} className="border border-gray-200 bg-white text-xs font-bold text-gray-800 py-2">
                                <div>{d.weekday_name}</div>
                                <div className="text-[11px] font-normal text-gray-500">{d.date_label}</div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {hoursLabels.map((hlabel, rowIdx) => (
                        <tr key={hlabel}>
                            <td className="border border-gray-200 text-xs text-gray-600 text-center bg-white">{hlabel}</td>
                            {days.map((d) => {
                                const cell = d.hours[rowIdx];
                                const testid = `cell-${d.date}-${String(cell.hour).padStart(2, "0")}`;
                                return (
                                    <td key={d.date + hlabel} className="p-0">
                                        <div
                                            className={cellClass(cell, d)}
                                            onClick={() => onCellClick && onCellClick(d, cell)}
                                            data-testid={testid}
                                            data-status={cell.status}
                                        >
                                            {cell.status === "available" && cell.start_minute === 30 && (
                                                <span className="text-[11px] font-bold">inizio {cell.start_label}</span>
                                            )}
                                        </div>
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export function PublicMobileDay({ weekData, dayIndex, setDayIndex, selection, onCellClick }) {
    const days = weekData?.days || [];
    const touchStart = useRef(null);
    if (days.length === 0) return null;
    // Show 3 days at a time; groupIndex derived from dayIndex
    const groupIndex = Math.min(Math.floor(dayIndex / 3), Math.max(0, Math.ceil(days.length / 3) - 1));
    const numGroups = Math.ceil(days.length / 3);
    const visibleDays = days.slice(groupIndex * 3, groupIndex * 3 + 3);
    const goPrev = () => setDayIndex(Math.max(0, (groupIndex - 1) * 3));
    const goNext = () => setDayIndex(Math.min(days.length - 1, (groupIndex + 1) * 3));

    const cellClass = (h, day) => {
        const isSel = selection && selection.date === day.date && selection.startHour === h.hour;
        const isNext = selection && selection.date === day.date && (selection.startHour + 1) === h.hour;
        if (h.status === "available") {
            let base = "agenda-cell agenda-cell-mobile agenda-cell-available";
            if (isSel) base += " agenda-cell-selected agenda-cell-selected-top";
            if (isNext) base += " agenda-cell-selected agenda-cell-selected-bottom";
            return base;
        }
        return "agenda-cell agenda-cell-mobile agenda-cell-unavailable";
    };

    // Swipe handlers
    const onTouchStart = (e) => { touchStart.current = e.changedTouches[0].clientX; };
    const onTouchEnd = (e) => {
        if (touchStart.current == null) return;
        const dx = e.changedTouches[0].clientX - touchStart.current;
        if (Math.abs(dx) > 60) { if (dx < 0) goNext(); else goPrev(); }
        touchStart.current = null;
    };

    const rangeLabel = visibleDays.length > 0
        ? `${visibleDays[0].weekday_name.slice(0,3)} – ${visibleDays[visibleDays.length-1].weekday_name.slice(0,3)}`
        : "";

    return (
        <div className="w-full" data-testid="agenda-mobile" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            <div className="flex items-center justify-between mb-3 border-b border-gray-200 pb-3">
                <button className="p-2 text-gray-700 disabled:text-gray-300" onClick={goPrev} disabled={groupIndex <= 0} data-testid="mobile-prev-day" aria-label="Giorni precedenti"><ChevronLeft size={22} /></button>
                <div className="text-center">
                    <div className="text-sm font-bold">{rangeLabel}</div>
                    <div className="text-[11px] text-gray-500">{groupIndex + 1} / {numGroups}</div>
                </div>
                <button className="p-2 text-gray-700 disabled:text-gray-300" onClick={goNext} disabled={groupIndex >= numGroups - 1} data-testid="mobile-next-day" aria-label="Giorni successivi"><ChevronRight size={22} /></button>
            </div>
            <div className="w-full">
                <table className="w-full border-collapse table-fixed">
                    <colgroup>
                        <col style={{ width: "42px" }} />
                        <col />
                        <col />
                        <col />
                    </colgroup>
                    <thead>
                        <tr>
                            <th className="border border-gray-200 bg-white text-[10px] font-bold text-gray-700 py-1">Ora</th>
                            {visibleDays.map((d) => (
                                <th key={d.date} className="border border-gray-200 bg-white text-[11px] font-bold text-gray-800 py-1 overflow-hidden">
                                    <div className="truncate px-1">{d.weekday_name}</div>
                                    <div className="text-[10px] font-normal text-gray-500">{d.date.split("-")[2]}/{d.date.split("-")[1]}</div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {(weekData?.hours_labels || []).map((hlabel, rowIdx) => (
                            <tr key={hlabel}>
                                <td className="border border-gray-200 text-[11px] text-gray-600 text-center bg-white">{hlabel}</td>
                                {visibleDays.map((d) => {
                                    const cell = d.hours[rowIdx];
                                    const testid = `cell-${d.date}-${String(cell.hour).padStart(2, "0")}`;
                                    return (
                                        <td key={d.date + hlabel} className="p-[2px] align-middle">
                                            <div className={cellClass(cell, d) + " rounded-sm cell-content"} onClick={() => onCellClick && onCellClick(d, cell)} data-testid={testid} data-status={cell.status}>
                                                {cell.status === "available" && cell.start_minute === 30 && (
                                                    <span className="text-[10px] font-bold leading-tight text-center px-0.5 break-words">inizio<br/>{cell.start_label}</span>
                                                )}
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="text-[11px] text-gray-500 mt-2 text-center">Swipe orizzontale o usa le frecce per cambiare gruppo di giorni</div>
        </div>
    );
}

/**
 * AdminAgendaGrid - half-hourly rows. Supports:
 * - single click toggle (block/unblock)
 * - click on booked cell -> onBookingClick
 * - Shift+Arrow multi-selection + Enter to apply toggle
 * - Shift+Click to extend selection
 * - Escape clears selection
 * - Mobile: long-press on a cell to enter selection, then tap another cell to select range, then use action bar
 */
export function AdminAgendaGrid({ weekData, onToggleCell, onBookingClick, onBulkAction }) {
    const days = weekData?.days || [];
    const slotLabels = weekData?.slot_labels || [];
    const [anchor, setAnchor] = useState(null); // {dayIdx, slotIdx}
    const [focus, setFocus] = useState(null);
    const containerRef = useRef(null);

    const clearSel = () => { setAnchor(null); setFocus(null); };

    const inRect = (dayIdx, slotIdx) => {
        if (!anchor || !focus) return false;
        const d1 = Math.min(anchor.dayIdx, focus.dayIdx), d2 = Math.max(anchor.dayIdx, focus.dayIdx);
        const s1 = Math.min(anchor.slotIdx, focus.slotIdx), s2 = Math.max(anchor.slotIdx, focus.slotIdx);
        return dayIdx >= d1 && dayIdx <= d2 && slotIdx >= s1 && slotIdx <= s2;
    };

    const getSelectedSlots = useCallback(() => {
        if (!anchor || !focus) return [];
        const out = [];
        const d1 = Math.min(anchor.dayIdx, focus.dayIdx), d2 = Math.max(anchor.dayIdx, focus.dayIdx);
        const s1 = Math.min(anchor.slotIdx, focus.slotIdx), s2 = Math.max(anchor.slotIdx, focus.slotIdx);
        for (let di = d1; di <= d2; di++) {
            const day = days[di]; if (!day) continue;
            for (let si = s1; si <= s2; si++) {
                const slot = day.slots[si]; if (!slot) continue;
                out.push({ date: day.date, hour: slot.hour, minute: slot.minute, status: slot.status });
            }
        }
        return out;
    }, [anchor, focus, days]);

    // Keyboard handling
    useEffect(() => {
        const onKey = (e) => {
            if (!anchor) return;
            if (e.key === "Escape") { clearSel(); return; }
            if (e.key === "Enter") {
                e.preventDefault();
                const sel = getSelectedSlots();
                if (sel.length === 0) return;
                // Skip booked ones: if all non-booked are blocked -> unblock, else block
                const nonBooked = sel.filter((s) => s.status !== "booked");
                if (nonBooked.length === 0) return;
                const allBlocked = nonBooked.every((s) => s.status === "blocked");
                const action = allBlocked ? "unblock" : "block";
                onBulkAction && onBulkAction(action, sel);
                return;
            }
            const dxdy = { ArrowUp: [0,-1], ArrowDown: [0,1], ArrowLeft: [-1,0], ArrowRight: [1,0] }[e.key];
            if (dxdy && e.shiftKey) {
                e.preventDefault();
                const [dx, dy] = dxdy;
                const cur = focus || anchor;
                const nd = Math.max(0, Math.min(days.length - 1, cur.dayIdx + dx));
                const ns = Math.max(0, Math.min(slotLabels.length - 1, cur.slotIdx + dy));
                setFocus({ dayIdx: nd, slotIdx: ns });
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [anchor, focus, days, slotLabels.length, getSelectedSlots, onBulkAction]);

    const [dragging, setDragging] = useState(false);

    const onMouseDown = (e, dayIdx, slotIdx) => {
        if (e.shiftKey && anchor) {
            setFocus({ dayIdx, slotIdx });
            return;
        }
        const day = days[dayIdx]; const slot = day?.slots[slotIdx];
        if (slot && slot.status === "booked") {
            if (onBookingClick && slot.booking_id) onBookingClick(slot.booking_id);
            return;
        }
        setAnchor({ dayIdx, slotIdx });
        setFocus({ dayIdx, slotIdx });
        setDragging(true);
    };

    const onMouseEnter = (dayIdx, slotIdx) => {
        if (dragging) setFocus({ dayIdx, slotIdx });
    };

    const onMouseUp = () => {
        setDragging(false);
        // if single cell selection => immediate toggle
        if (anchor && focus && anchor.dayIdx === focus.dayIdx && anchor.slotIdx === focus.slotIdx) {
            const day = days[anchor.dayIdx]; const slot = day.slots[anchor.slotIdx];
            if (slot && slot.status !== "booked") {
                onToggleCell && onToggleCell(day.date, slot.hour, slot.minute, slot.status);
                clearSel();
            }
        }
    };

    useEffect(() => {
        const up = () => setDragging(false);
        window.addEventListener("mouseup", up);
        return () => window.removeEventListener("mouseup", up);
    }, []);

    const cellClass = (slot, inSel) => {
        let base = "agenda-cell agenda-cell-half";
        if (slot.status === "available") base += " agenda-cell-available";
        else if (slot.status === "blocked") base += " agenda-cell-blocked";
        else if (slot.status === "booked") base += " agenda-cell-booked";
        if (inSel) base += " agenda-cell-multi-select";
        return base;
    };

    // Compute selection info for action bar
    const selSlots = anchor && focus ? getSelectedSlots() : [];
    const nonBooked = selSlots.filter((s) => s.status !== "booked");
    const allBlocked = nonBooked.length > 0 && nonBooked.every((s) => s.status === "blocked");

    return (
        <div ref={containerRef} tabIndex={0} className="w-full">
            {selSlots.length > 1 && (
                <div className="mb-2 flex items-center justify-between border border-green-700 bg-green-50 px-3 py-2 text-sm" data-testid="selection-action-bar">
                    <span><b>{nonBooked.length}</b> celle selezionate. Premi <b>Invio</b> per {allBlocked ? "sbloccare" : "bloccare"}, <b>Esc</b> per annullare.</span>
                    <div className="flex gap-2">
                        <button className="px-3 py-1 border border-gray-300 bg-white text-sm" onClick={clearSel} data-testid="sel-cancel-btn">Annulla</button>
                        <button className="px-3 py-1 border border-green-700 bg-green-700 text-white text-sm" onClick={() => { onBulkAction && onBulkAction(allBlocked ? "unblock" : "block", selSlots); clearSel(); }} data-testid="sel-apply-btn">
                            {allBlocked ? "Sblocca" : "Blocca"} selezione
                        </button>
                    </div>
                </div>
            )}
            <div className="w-full overflow-x-auto" data-testid="admin-grid">
                <table className="w-full border-collapse select-none" style={{ minWidth: 720 }}>
                    <thead>
                        <tr>
                            <th className="w-20 border border-gray-200 bg-white text-xs font-bold text-gray-700 py-2">Ora</th>
                            {days.map((d) => (
                                <th key={d.date} className="border border-gray-200 bg-white text-xs font-bold text-gray-800 py-2">
                                    <div>{d.weekday_name}</div>
                                    <div className="text-[11px] font-normal text-gray-500">{d.date_label}</div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {slotLabels.map((label, si) => {
                            const isHour = label.endsWith(":00");
                            return (
                                <tr key={label}>
                                    <td className={`border border-gray-200 text-[11px] text-center bg-white ${isHour ? "text-gray-700 font-semibold" : "text-gray-400"}`}>{label}</td>
                                    {days.map((d, di) => {
                                        const slot = d.slots[si];
                                        if (!slot) return <td key={d.date+label} />;
                                        const inSel = inRect(di, si);
                                        const testid = `admin-cell-${d.date}-${slot.hour.toString().padStart(2,"0")}${slot.minute.toString().padStart(2,"0")}`;
                                        // Show name in booked cell only on the FIRST of a booking's 4 slots
                                        let showName = false;
                                        if (slot.status === "booked") {
                                            const prev = si > 0 ? d.slots[si-1] : null;
                                            if (!prev || prev.booking_id !== slot.booking_id) showName = true;
                                        }
                                        return (
                                            <td key={d.date+label} className="p-0" onMouseDown={(e) => onMouseDown(e, di, si)} onMouseEnter={() => onMouseEnter(di, si)} onMouseUp={onMouseUp}>
                                                <div className={cellClass(slot, inSel)} data-testid={testid} data-status={slot.status}>
                                                    {showName && (
                                                        <span className="text-[10px] font-bold text-gray-800 truncate px-1" title={slot.booking_name}>
                                                            <UserIcon size={10} className="inline mr-0.5" />{slot.booking_name}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="text-[11px] text-gray-500 mt-2">
                Suggerimento: click su una cella per bloccare/sbloccare. <b>Shift+Frecce</b> o trascina per selezionare più celle, poi premi <b>Invio</b> per applicare.
            </div>
        </div>
    );
}

export function AdminMobileDay({ weekData, dayIndex, setDayIndex, onToggleCell, onBookingClick, onBulkAction }) {
    const days = weekData?.days || [];
    const slotLabels = weekData?.slot_labels || [];
    const [selSet, setSelSet] = useState(new Set()); // set of "date|hh:mm" keys
    const longPressTimer = useRef(null);
    const touchStart = useRef(null);

    if (days.length === 0) return null;
    if (days.length === 0) return null;

    const groupIndex = Math.min(Math.floor(dayIndex / 3), Math.max(0, Math.ceil(days.length / 3) - 1));
    const numGroups = Math.ceil(days.length / 3);
    const visibleDays = days.slice(groupIndex * 3, groupIndex * 3 + 3);
    const goPrev = () => setDayIndex(Math.max(0, (groupIndex - 1) * 3));
    const goNext = () => setDayIndex(Math.min(days.length - 1, (groupIndex + 1) * 3));

    const selMode = selSet.size > 0;
    const cellKey = (date, si) => `${date}|${si}`;
    const toggleSel = (date, si) => {
        const k = cellKey(date, si);
        const s = new Set(selSet);
        s.has(k) ? s.delete(k) : s.add(k);
        setSelSet(s);
    };
    const beginLongPress = (date, si) => {
        longPressTimer.current = setTimeout(() => {
            const s = new Set(); s.add(cellKey(date, si)); setSelSet(s);
        }, 400);
    };
    const cancelLongPress = () => { if (longPressTimer.current) clearTimeout(longPressTimer.current); };

    const onTouchStart = (e) => { touchStart.current = e.changedTouches[0].clientX; };
    const onTouchEnd = (e) => {
        cancelLongPress();
        if (selMode || touchStart.current == null) { touchStart.current = null; return; }
        const dx = e.changedTouches[0].clientX - touchStart.current;
        touchStart.current = null;
        if (Math.abs(dx) > 60) { if (dx < 0) goNext(); else goPrev(); }
    };

    const applyBulk = (action) => {
        const slots = [];
        for (const key of selSet) {
            const [date, siStr] = key.split("|");
            const si = parseInt(siStr, 10);
            const day = days.find((x) => x.date === date);
            if (!day) continue;
            const sl = day.slots[si]; if (!sl) continue;
            slots.push({ date, hour: sl.hour, minute: sl.minute, status: sl.status });
        }
        onBulkAction && onBulkAction(action, slots);
        setSelSet(new Set());
    };

    const cellClass = (slot, sel) => {
        let base = "agenda-cell agenda-cell-mobile-half";
        if (slot.status === "available") base += " agenda-cell-available";
        else if (slot.status === "blocked") base += " agenda-cell-blocked";
        else if (slot.status === "booked") base += " agenda-cell-booked";
        if (sel) base += " agenda-cell-multi-select";
        return base;
    };

    const selArr = [];
    for (const key of selSet) {
        const [date, siStr] = key.split("|");
        const si = parseInt(siStr, 10);
        const day = days.find((x) => x.date === date);
        if (day) { const sl = day.slots[si]; if (sl) selArr.push(sl); }
    }
    const nonBooked = selArr.filter((s) => s.status !== "booked");
    const allBlocked = nonBooked.length > 0 && nonBooked.every((s) => s.status === "blocked");

    const rangeLabel = visibleDays.length > 0
        ? `${visibleDays[0].weekday_name.slice(0,3)} – ${visibleDays[visibleDays.length-1].weekday_name.slice(0,3)}`
        : "";

    return (
        <div className="w-full" data-testid="admin-mobile" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            <div className="flex items-center justify-between mb-3 border-b border-gray-200 pb-3">
                <button className="p-2 text-gray-700 disabled:text-gray-300" onClick={goPrev} disabled={groupIndex <= 0} aria-label="Giorni precedenti"><ChevronLeft size={22} /></button>
                <div className="text-center">
                    <div className="text-sm font-bold">{rangeLabel}</div>
                    <div className="text-[11px] text-gray-500">{groupIndex + 1} / {numGroups}</div>
                </div>
                <button className="p-2 text-gray-700 disabled:text-gray-300" onClick={goNext} disabled={groupIndex >= numGroups - 1} aria-label="Giorni successivi"><ChevronRight size={22} /></button>
            </div>

            {selMode && (
                <div className="mb-2 flex items-center justify-between border border-green-700 bg-green-50 px-3 py-2 text-sm">
                    <span><b>{nonBooked.length}</b> selezionate</span>
                    <div className="flex gap-2">
                        <button className="px-3 py-1 border border-gray-300 bg-white text-sm" onClick={() => setSelSet(new Set())}>Annulla</button>
                        <button className="px-3 py-1 border border-green-700 bg-green-700 text-white text-sm" onClick={() => applyBulk(allBlocked ? "unblock" : "block")}>{allBlocked ? "Sblocca" : "Blocca"}</button>
                    </div>
                </div>
            )}

            <div className="w-full">
                <table className="w-full border-collapse select-none table-fixed">
                    <colgroup>
                        <col style={{ width: "42px" }} />
                        <col />
                        <col />
                        <col />
                    </colgroup>
                    <thead>
                        <tr>
                            <th className="border border-gray-200 bg-white text-[10px] font-bold text-gray-700 py-1">Ora</th>
                            {visibleDays.map((d) => (
                                <th key={d.date} className="border border-gray-200 bg-white text-[11px] font-bold text-gray-800 py-1 overflow-hidden">
                                    <div className="truncate px-1">{d.weekday_name}</div>
                                    <div className="text-[10px] font-normal text-gray-500">{d.date.split("-")[2]}/{d.date.split("-")[1]}</div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {slotLabels.map((label, si) => {
                            const isHour = label.endsWith(":00");
                            return (
                                <tr key={label}>
                                    <td className={`border border-gray-200 text-[11px] text-center bg-white ${isHour ? "text-gray-700 font-semibold" : "text-gray-400"}`}>{label}</td>
                                    {visibleDays.map((d) => {
                                        const slot = d.slots[si];
                                        if (!slot) return <td key={d.date+label} />;
                                        const sel = selSet.has(cellKey(d.date, si));
                                        let showName = false;
                                        if (slot.status === "booked") {
                                            const prev = si > 0 ? d.slots[si-1] : null;
                                            if (!prev || prev.booking_id !== slot.booking_id) showName = true;
                                        }
                                        const testid = `admin-cell-${d.date}-${slot.hour.toString().padStart(2,"0")}${slot.minute.toString().padStart(2,"0")}`;
                                        return (
                                            <td key={d.date+label} className="p-[2px] align-middle"
                                                onTouchStart={() => beginLongPress(d.date, si)}
                                                onTouchEnd={cancelLongPress}
                                                onTouchMove={cancelLongPress}
                                                onClick={() => {
                                                    if (selMode) { if (slot.status !== "booked") toggleSel(d.date, si); return; }
                                                    if (slot.status === "booked" && slot.booking_id) { onBookingClick && onBookingClick(slot.booking_id); return; }
                                                    if (slot.status !== "booked") onToggleCell && onToggleCell(d.date, slot.hour, slot.minute, slot.status);
                                                }}
                                            >
                                                <div className={cellClass(slot, sel) + " rounded-sm cell-content"} data-testid={testid} data-status={slot.status}>
                                                    {showName && (
                                                        <span className="text-[9px] font-bold text-gray-800 leading-tight text-center px-0.5 line-clamp-2">
                                                            <UserIcon size={9} className="inline mr-0.5" />{slot.booking_name}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="text-[11px] text-gray-500 mt-2 text-center">Swipe orizzontale per cambiare gruppo. Tieni premuto per selezione multipla.</div>
        </div>
    );
}
